// src/layouts/AdminLayout.tsx
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect, useRef, Suspense } from "react";
import { useAuth } from "../context/AuthProvider";
import { useApp } from "../context/AppContext";
import Bell from "../pages/components/Bell";
import { logout } from "../services/authService";
import FullScreenLoader from "../components/ui/FullScreenLoader";
import PushRegistrar from "../components/PushRegistrar";

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const app = useApp(); // { tenantId?, activeBranchId?, roles?, isAdmin? ... }

  // Drawer móvil
  const [mobileOpen, setMobileOpen] = useState(false);

  // Derivamos flags de boot del contexto, pero SIN bloquear indefinidamente
  const tenantReady = typeof app?.tenantId !== "undefined";
  const branchReady = typeof app?.activeBranchId !== "undefined";

  // Si tus páginas dependen sí o sí de branch/tenant, mantené este gate.
  // Para evitar "loop infinito", soltamos el gate tras 8s.
  const [giveUp, setGiveUp] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const waitingApp = !tenantReady || !branchReady;
    if (waitingApp && !timerRef.current) {
      timerRef.current = window.setTimeout(() => setGiveUp(true), 8000); // 8s
    }
    if (!waitingApp && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setGiveUp(false);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [tenantReady, branchReady]);

  // Calcular initials antes de early returns (hooks deben estar en el mismo orden)
  const initials = useMemo(() => {
    if (!user) return "AD";
    const n = user.displayName || user.email || "AD";
    return n.trim().slice(0, 2).toUpperCase();
  }, [user]);

  // Cerrar drawer al cambiar de ruta
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileOpen(false);
  }, [location.pathname]);

  // Evitar scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (mobileOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [mobileOpen]);

  // Loader mientras Auth aún resuelve
  if (authLoading || typeof user === "undefined") {
    return <FullScreenLoader message="Cargando sesión…" solidBackdrop />;
  }

  // (La falta de user no debería pasar por AdminRoute, pero dejamos safe-guard)
  if (!user) return null;

  async function onLogout() {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch {
      /* noop */
    }
  }

  // Clases para items de navegación
  const navItem = ({ isActive }: { isActive: boolean }) =>
    `block px-3 py-2 rounded ${isActive ? "bg-pastel-pink text-slate-900" : "hover:bg-pastel-pink/30 text-slate-900"}`;

  // NavList como componente para reutilizar en desktop y móvil
  const NavList = ({ onNavigate }: { onNavigate?: () => void }) => {
    const handleClick = () => {
      if (onNavigate) onNavigate();
    };
    return (
      <nav className="space-y-1">
        <NavLink to="/admin" end className={navItem} onClick={handleClick}>
          Inicio
        </NavLink>

        <div className="mt-4 text-xs uppercase text-slate-500 px-3">Clases</div>
        <NavLink to="/admin/classes" className={navItem} onClick={handleClick}>
          Listado
        </NavLink>
        <NavLink to="/admin/classes/new" className={navItem} onClick={handleClick}>
          Nueva clase
        </NavLink>
        <NavLink to="/admin/classes/bulk" className={navItem} onClick={handleClick}>
          Programación mensual
        </NavLink>
        <NavLink to="/admin/classes/types" className={navItem}>
          Tipos de clase
        </NavLink>

        <div className="mt-4 text-xs uppercase text-slate-500 px-3">Rutinas</div>
        <NavLink to="/admin/routines" className={navItem} onClick={handleClick}>
          Rutinas
        </NavLink>
        <NavLink to="/admin/routines/exercises" className={navItem} onClick={handleClick}>
          Ejercicios
        </NavLink>
        <NavLink to="/app/routines" className={navItem} onClick={handleClick}>
          Mis rutinas
        </NavLink>

        <div className="mt-4 text-xs uppercase text-slate-500 px-3">Reportes</div>
        <NavLink to="/admin/reports/occupancy" className={navItem} onClick={handleClick}>
          Ocupación
        </NavLink>
        <NavLink to="/admin/reports/checkins" className={navItem} onClick={handleClick}>
          Asistencias
        </NavLink>

        <div className="mt-4 text-xs uppercase text-slate-500 px-3">Comunicaciones</div>
        <NavLink to="/admin/notifications" className={navItem} onClick={handleClick}>
          Notificaciones
        </NavLink>

        <div className="mt-4 text-xs uppercase text-slate-500 px-3">Gestión</div>
        <NavLink to="/admin/users" className={navItem} onClick={handleClick}>
          Usuarios
        </NavLink>
        <NavLink to="/admin/plans" className={navItem} onClick={handleClick}>
          Planes
        </NavLink>

        <div className="mt-4 text-xs uppercase text-slate-500 px-3">Herramientas</div>
        <NavLink to="/admin/timer" className={navItem} onClick={handleClick}>
          Cronómetro (30s)
        </NavLink>
      </nav>
    );
  };

  // Gate de arranque del contexto "razonable":
  const waitingApp = !tenantReady || !branchReady;
  if (waitingApp && !giveUp) {
    return <FullScreenLoader message="Inicializando panel de administración…" solidBackdrop />;
  }

  const tenantId = (app?.tenantId as string) || "";
  const uid = user?.uid || "";

  return (
    <div className="min-h-screen bg-pastel-cream text-slate-900 body-safe">
      {/* Registrador invisible de push */}
      {tenantId && uid ? <PushRegistrar tenantId={tenantId} uid={uid} /> : null}

      {/* Topbar */}
      <header className="border-b border-pastel-pink/60 sticky top-0 z-40 bg-pastel-cream/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Botón hamburguesa (solo móvil) */}
            <button
              type="button"
              className="inline-flex md:hidden items-center justify-center rounded-md p-2 hover:bg-pastel-pink/30 focus:outline-none"
              aria-label="Abrir menú"
              aria-controls="admin-mobile-drawer"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
                <path d="M3 6h18v2H3zM3 11h18v2H3zM3 16h18v2H3z" />
              </svg>
            </button>

            <a href="/admin" className="font-semibold tracking-wide select-none">
              Entrenarte Admin
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Bell />
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-2 py-1 rounded border border-pastel-pink/60 hover:bg-pastel-pink/30"
              title="Cerrar sesión"
            >
              <div className="w-6 h-6 rounded-full bg-pastel-pink grid place-items-center text-xs">{initials}</div>
              <div className="text-xs text-slate-700 truncate max-w-[180px]">
                {user.displayName || user.email || "Admin"}
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Drawer móvil */}
      <div
        id="admin-mobile-drawer"
        aria-hidden={!mobileOpen}
        className={`md:hidden ${mobileOpen ? "fixed" : "hidden"} inset-0 z-50`}
      >
        {/* Overlay */}
        <button
          aria-label="Cerrar menú"
          className="absolute inset-0 bg-black/40"
          onClick={() => setMobileOpen(false)}
        />
        {/* Panel */}
        <div
          role="dialog"
          aria-modal="true"
          className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-2xl ring-1 ring-black/5 flex flex-col"
        >
          <div className="h-14 shrink-0 border-b border-neutral-200 px-3 flex items-center justify-between">
            <span className="font-semibold">Navegación</span>
            <button
              type="button"
              className="rounded-md p-2 hover:bg-neutral-100 active:bg-neutral-200"
              aria-label="Cerrar menú"
              onClick={() => setMobileOpen(false)}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4l-6.3 6.31-1.41-1.42L9.17 12 2.88 5.71 4.29 4.3l6.3 6.3 6.29-6.3z" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {/* eslint-disable-next-line react-hooks/static-components */}
            <NavList onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      </div>

      {/* Body con sidebar + contenido */}
      <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-6 grid grid-cols-12 gap-6">
        {/* Sidebar desktop */}
        <aside className="hidden md:flex md:flex-col md:col-span-3 lg:col-span-2">
          {/* eslint-disable-next-line react-hooks/static-components */}
          <NavList />
        </aside>

        {/* Contenido */}
        <main className="col-span-12 md:col-span-9 lg:col-span-10">
          <div className="container-page">
            <Suspense fallback={<FullScreenLoader message="Cargando módulo…" />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
